import { Navigate, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from '@doccareplus/i18n';
import { Button, Card, SkeletonCard } from '@doccareplus/ui';
import { usePortalHomeQuery, usePortalProfilesQuery } from '@/features/profiles';

export function ActiveProfileRoute() {
  const { t } = useTranslation('portal');
  const navigate = useNavigate();
  const profiles = usePortalProfilesQuery();
  const home = usePortalHomeQuery();

  if (profiles.isLoading || home.isLoading) {
    return <SkeletonCard />;
  }

  if (profiles.isError || home.isError) {
    return (
      <Card role="alert" className="mx-auto max-w-[560px] p-5 text-center">
        <h2 className="text-[17px] font-semibold">
          {t('profile.loadFailedTitle')}
        </h2>
        <p className="mx-auto mt-1 max-w-[48ch] text-[13px] leading-5 text-[var(--text-2)]">
          {t('profile.loadFailedDescription')}
        </p>
        <Button
          className="mt-4"
          variant="outline"
          onClick={() => {
            void profiles.refetch();
            void home.refetch();
          }}
        >
          {t('actions.retry')}
        </Button>
      </Card>
    );
  }

  const activeLinkId = profiles.data?.activeLinkId;
  const hasActiveProfile = Boolean(
    activeLinkId && profiles.data?.profiles.some((profile) =>
      profile.linkId === activeLinkId && profile.isActiveSelection)
  );
  const activeProfile = profiles.data?.profiles.find((profile) =>
    profile.linkId === activeLinkId && profile.isActiveSelection
  );

  if (hasActiveProfile && home.data?.isActiveProfilePortalAvailable === false) {
    return (
      <Card role="status" className="mx-auto max-w-[560px] p-5 text-center">
        <h2 className="text-[17px] font-semibold">
          {t('profile.portalUnavailableTitle', 'Portal access is not available for this clinic')}
        </h2>
        <p className="mx-auto mt-1 max-w-[48ch] text-[13px] leading-5 text-[var(--text-2)]">
          {t('profile.portalUnavailableDescription', 'Your profile stays connected, but this clinic has paused online records and appointment access.')}
        </p>
        <div className="mt-4 flex justify-center gap-2">
          <Button variant="outline" onClick={() => navigate('/profile')}>
            {t('tabs.profile', 'Profiles')}
          </Button>
          <Button onClick={() => navigate('/find-care')}>
            {t('home.findClinic', 'Find a clinic')}
          </Button>
        </div>
      </Card>
    );
  }

  return hasActiveProfile
    ? <Outlet context={{ isArchived: Boolean(activeProfile?.isArchived) }} />
    : <Navigate to="/profile" replace />;
}
